import { useEffect, useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import Field from '../components/Field.jsx'
import FormAlert from '../components/FormAlert.jsx'
import ThemeToggle from '../components/ThemeToggle.jsx'
import { useAuth } from '../context/AuthContext.jsx'
import { api, ApiError } from '../lib/api.js'

const MIN_PASSWORD = 12

/**
 * The format hint. It is the only place that says what a username may contain,
 * so a refusal must not be allowed to take it off the screen: the blur note and
 * the submit refusal are the same sentence, and printing both left the person
 * told twice that the name was taken and never told what a legal one looks
 * like.
 */
const USERNAME_HINT = 'Letters, numbers and underscores. This is your profile address.'

export default function Welcome() {
  const [params] = useSearchParams()
  const navigate = useNavigate()
  const { signup } = useAuth()

  const [form, setForm] = useState({
    invite_code: params.get('code') || '',
    email: '',
    username: '',
    display_name: '',
    password: '',
  })
  const [fieldErrors, setFieldErrors] = useState({})
  const [alert, setAlert] = useState(null)
  const [busy, setBusy] = useState(false)
  const [usernameNote, setUsernameNote] = useState(null)

  useEffect(() => {
    document.title = 'Join Sonologue'
  }, [])

  const set = (key) => (event) => {
    setForm((prev) => ({ ...prev, [key]: event.target.value }))
    setFieldErrors((prev) => ({ ...prev, [key]: undefined }))
  }

  async function checkUsername() {
    if (!form.username) return setUsernameNote(null)
    try {
      const result = await api.usernameAvailable(form.username)
      setUsernameNote(result.available ? { ok: true, text: 'Available.' } : { ok: false, text: result.reason })
    } catch {
      setUsernameNote(null)
    }
  }

  async function onSubmit(event) {
    event.preventDefault()
    setAlert(null)
    setFieldErrors({})
    setBusy(true)
    try {
      await signup(form)
      navigate('/', { replace: true })
    } catch (error) {
      if (error instanceof ApiError) {
        // The API's own codes drive which field the message lands on, so the
        // wording stays in one place rather than being re-guessed here.
        const byCode = {
          'invite-invalid': 'invite_code',
          'invite-expired': 'invite_code',
          'invite-revoked': 'invite_code',
          'invite-used': 'invite_code',
          'username-taken': 'username',
          'username-invalid': 'username',
          'username-reserved': 'username',
          'email-taken': 'email',
        }
        const field = byCode[error.code]
        if (field) setFieldErrors({ [field]: error.message })
        else if (Object.keys(error.fieldErrors).length) setFieldErrors(error.fieldErrors)
        else setAlert(error.message)
      } else {
        setAlert('Something went wrong. Try again.')
      }
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="auth-shell">
      <div className="absolute top-4 right-4">
        <ThemeToggle />
      </div>
      <div className="card card-pad auth-card fade-up">
        <h1 className="auth-heading">Join Sonologue</h1>
        <p className="auth-sub">
          The alpha is invite-only. Enter the code you were sent to make an account.
        </p>

        <FormAlert>{alert}</FormAlert>

        <form onSubmit={onSubmit} noValidate>
          <Field
            label="Invite code"
            value={form.invite_code}
            onChange={set('invite_code')}
            error={fieldErrors.invite_code}
            inputClassName="mono"
            autoComplete="off"
            autoCapitalize="characters"
            spellCheck="false"
            required
          />
          <Field
            label="Email"
            type="email"
            value={form.email}
            onChange={set('email')}
            error={fieldErrors.email}
            autoComplete="email"
            required
          />
          <Field
            label="Username"
            value={form.username}
            onChange={set('username')}
            onBlur={checkUsername}
            error={fieldErrors.username}
            hint={fieldErrors.username || !usernameNote ? USERNAME_HINT : usernameNote.text}
            autoComplete="username"
            required
          />
          <Field
            label="Display name"
            value={form.display_name}
            onChange={set('display_name')}
            error={fieldErrors.display_name}
            hint="Optional — defaults to your username."
            autoComplete="nickname"
          />
          <Field
            label="Password"
            type="password"
            value={form.password}
            onChange={set('password')}
            error={fieldErrors.password}
            hint={`At least ${MIN_PASSWORD} characters. Length beats punctuation.`}
            autoComplete="new-password"
            required
          />

          <button type="submit" className="btn btn-primary btn-block" disabled={busy}>
            {busy ? 'Creating your account…' : 'Create account'}
          </button>
        </form>

        <p className="auth-alt">
          Already have an account? <Link to="/login">Sign in</Link>
        </p>
      </div>
    </div>
  )
}
